import { useEffect, useRef, useState } from "react";

import { getEnvironmentVariable } from "@/lib/environment";
import { groupKitDocumentControllerGetDocumentAuth } from "@/lib/gen-api";
import { TiptapCollabProvider } from "@hocuspocus/provider";
import * as Y from "yjs";
import { useGroupUuid } from "../../hooks/use-group-uuid";
import { useKitUuid } from "../../hooks/use-kit-uuid";

const TIPTAP_SERVER_ID = getEnvironmentVariable("TIPTAP_SERVER_ID");

export function useCollabProvider(
  documentUuid: string | null,
  path: string | null,
): {
  provider: TiptapCollabProvider | null;
  document: Y.Doc | null;
  token: string | null;
} {
  const kitUuid = useKitUuid();
  const groupUuid = useGroupUuid();
  const ydocRef = useRef<Y.Doc>(new Y.Doc());
  const providerRef = useRef<TiptapCollabProvider | null>(null);
  const lastPathRef = useRef<string | null>(null);

  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    async function initProvider() {
      if (lastPathRef.current === path) {
        return;
      }

      if (providerRef.current) {
        providerRef.current.destroy();
      }

      ydocRef.current.destroy();
      ydocRef.current = new Y.Doc();

      if (path === null || !kitUuid || !groupUuid || !documentUuid) {
        providerRef.current = null;
        lastPathRef.current = path;
        setToken(null);
        return;
      }

      try {
        const response = await groupKitDocumentControllerGetDocumentAuth({
          path: {
            kitUuid,
            groupUuid,
            documentUuid,
          },
          body: {
            path,
          },
        });

        const authToken = response.data?.token;

        providerRef.current = new TiptapCollabProvider({
          name: path,
          appId: TIPTAP_SERVER_ID,
          token: authToken,
          document: ydocRef.current,
        });

        setToken(authToken ?? null);
        lastPathRef.current = path;
      } catch (error) {
        console.error("Failed to initialize provider:", error);
        providerRef.current = null;
      }
    }

    initProvider();

    return () => {
      if (providerRef.current) {
        providerRef.current.destroy();
      }
    };
  }, [path]);

  return {
    provider: providerRef.current ?? null,
    document: ydocRef.current,
    token,
  };
}
